import { apiClient, publicApiClient } from './api';

export type InquiryMode = 'collect_only' | 'estimate';
export type InquiryStage =
  | 'new'
  | 'reviewing'
  | 'awaiting_information'
  | 'sourcing'
  | 'quoted'
  | 'awaiting_confirmation'
  | 'booked'
  | 'lost'
  | 'spam'
  | 'cancelled';
export interface InquirySettings {
  enabled: boolean;
  chatEnabled?: boolean;
  mode: InquiryMode;
  operatorEmail: string | null;
  notice: string;
}
export interface InquiryFormConfig {
  enabled: boolean;
  chatEnabled?: boolean;
  mode: InquiryMode;
  companyName: string;
  timezone: string;
  vehicleTypes: string[];
  notice: string;
}
export interface BookingPlanVehicleRequirement {
  vehicleType?: string;
  quantity?: number;
  passengerCapacity?: number;
  notes?: string;
}
export interface BookingPlanSegment {
  label?: string;
  startDate?: string;
  endDate?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  passengerCount?: number;
  vehicleRequirements?: BookingPlanVehicleRequirement[];
  stops?: string[];
  notes?: string;
}
export interface InquiryBookingPlan {
  version: 1;
  segments: BookingPlanSegment[];
  vehicleRequirements?: BookingPlanVehicleRequirement[];
  notes?: string;
}
export interface InquiryTrip {
  bookingPlan?: InquiryBookingPlan;
  startDate?: string;
  endDate?: string;
  timezone: string;
  pickupLocation: string;
  dropoffLocation: string;
  passengerCount?: number;
  vehicleType?: string;
  serviceNotes?: string;
  stops?: string[];
  serviceType?: string;
}
export interface InquirySubmission {
  submissionId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
  trip: InquiryTrip;
  mode?: InquiryMode;
}
export interface InquiryReceipt {
  id: string;
  reference: string;
  stage: string;
  version: number;
  replayed: boolean;
  notificationStatus: string;
}
export interface InquiryEstimate {
  status: 'estimated' | 'needs_review';
  pricing: null | {
    subtotal: number;
    serviceCharge: number;
    gratuity: number;
    tax: number;
    total: number;
    depositDue: number;
  };
  requirements: {
    vehicleType: string;
    quantity: number;
    availableCount: number;
    availabilityStatus: string;
    unitRate: number;
    subtotal: number;
  }[];
  notices: string[];
  inventoryCommitted: false;
}
export interface TripInquiry {
  id: string;
  reference: string;
  stage: InquiryStage;
  version: number;
  created_at: string;
  contact_json: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    companyName?: string;
  };
  trip_json: InquiryTrip;
  estimate_json: InquiryEstimate | null;
  customer_id: string | null;
  active_quote_id: string | null;
  converted_reservation_id: string | null;
  notes?: string;
  notifications?: {
    id: string;
    kind: string;
    status: string;
    attempts: number;
    providerStatus?: string;
    errorCode?: string;
    updatedAt: string;
  }[];
}
export const tripInquiryAPI = {
  form: () => publicApiClient.get<InquiryFormConfig>('/trip-inquiries/form'),
  estimate: (trip: InquiryTrip) =>
    publicApiClient.post<InquiryEstimate>('/trip-inquiries/estimate', { trip }),
  submit: (data: InquirySubmission) =>
    publicApiClient.post<InquiryReceipt>('/trip-inquiries', data),
  settings: () =>
    apiClient.get<InquirySettings>('/admin/trip-inquiries/settings'),
  updateSettings: (data: InquirySettings) =>
    apiClient.patch<InquirySettings>('/admin/trip-inquiries/settings', data),
  list: (params: {
    stage?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) =>
    apiClient.get<{
      items: TripInquiry[];
      total: number;
      page: number;
      limit: number;
    }>('/admin/trip-inquiries', { params }),
  detail: (id: string) =>
    apiClient.get<TripInquiry>(
      `/admin/trip-inquiries/${encodeURIComponent(id)}`
    ),
  editTrip: (id: string, expectedVersion: number, trip: InquiryTrip) =>
    apiClient.patch<TripInquiry>(
      `/admin/trip-inquiries/${encodeURIComponent(id)}`,
      { expectedVersion, trip }
    ),
  retryNotifications: (id: string) =>
    apiClient.post(
      `/admin/trip-inquiries/${encodeURIComponent(id)}/notifications/retry`
    ),
  stage: (id: string, stage: string, expectedVersion: number, note?: string) =>
    apiClient.patch<TripInquiry>(
      `/admin/trip-inquiries/${encodeURIComponent(id)}`,
      { stage, expectedVersion, note }
    ),
  quote: (id: string, expectedVersion: number) =>
    apiClient.post<{ inquiry: TripInquiry; quote: { id: string } }>(
      `/admin/trip-inquiries/${encodeURIComponent(id)}/quote`,
      { expectedVersion }
    ),
};
export function inquiryError(error: unknown): string {
  const detail = (
    error as {
      response?: { data?: { message?: string | string[] } };
      message?: string;
    }
  )?.response?.data?.message;
  return Array.isArray(detail)
    ? detail.join('. ')
    : detail || 'We could not complete this request. Please try again.';
}
export const inquiryLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
export const inquiryMoney = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    value
  );

export interface SourcingProposal {
  id: string;
  providerCompanyId?: string;
  providerName: string;
  amountCents: number;
  currency: string;
  validUntil: string;
  conditions: string;
  capacityConfirmed: boolean;
  createdAt: string;
}
export interface SourcingRequest {
  id: string;
  inquiryId?: string;
  originCompanyId: string;
  originName: string;
  state: string;
  version: number;
  startAt: string;
  endAt: string;
  timezone: string;
  pickupArea: string;
  dropoffArea: string;
  requirements: string;
  selectedProposalId?: string;
  adoptedOfferId?: string;
  owner: boolean;
  proposals: SourcingProposal[];
}
export const tripSourcingAPI = {
  list: (view: 'owned' | 'available') =>
    apiClient.get<{ data: SourcingRequest[] }>('/admin/trip-sourcing', {
      params: { view },
    }),
  detail: (id: string) =>
    apiClient.get<SourcingRequest>(
      `/admin/trip-sourcing/${encodeURIComponent(id)}`
    ),
  publish: (data: {
    inquiryId: string;
    requestId: string;
    expectedVersion: number;
    requirements: string;
    pickupArea: string;
    dropoffArea: string;
  }) => apiClient.post<SourcingRequest>('/admin/trip-sourcing', data),
  propose: (
    id: string,
    data: {
      requestId: string;
      expectedVersion: number;
      amountCents: number;
      currency: 'USD';
      validUntil: string;
      conditions: string;
      capacityConfirmed: boolean;
    }
  ) =>
    apiClient.post<SourcingRequest>(
      `/admin/trip-sourcing/${encodeURIComponent(id)}/proposals`,
      data
    ),
  select: (
    id: string,
    proposalId: string,
    expectedVersion: number,
    requestId: string
  ) =>
    apiClient.post<SourcingRequest>(
      `/admin/trip-sourcing/${encodeURIComponent(id)}/select`,
      { proposalId, expectedVersion, requestId }
    ),
  approve: (
    id: string,
    data: {
      quoteId: string;
      expectedVersion: number;
      fulfillment: 'own' | 'partner';
      proposalId?: string;
    }
  ) =>
    apiClient.post<TripInquiry>(
      `/admin/trip-sourcing/inquiries/${encodeURIComponent(id)}/approve-quote`,
      data
    ),
  adopt: (id: string, expectedVersion: number, requestId: string) =>
    apiClient.post<SourcingRequest>(
      `/admin/trip-sourcing/${encodeURIComponent(id)}/adopt`,
      { expectedVersion, requestId }
    ),
};
